// Logical grid model: isometric projection, eight-direction stepping, and pathfinding.
import { WORLD } from '../config/constants';

export interface Tile {
  x: number;
  y: number;
}

export function tileKey(t: Tile): string {
  return `${t.x},${t.y}`;
}

/** Screen position of the TOP vertex of tile (x, y). */
export function toScreen(x: number, y: number, originX: number, originY: number): { sx: number; sy: number } {
  return {
    sx: (x - y) * (WORLD.TILE_W / 2) + originX,
    sy: (x + y) * (WORLD.TILE_H / 2) + originY,
  };
}

/** Screen position of the CENTER of tile (x, y). */
export function tileCenter(x: number, y: number, originX: number, originY: number): { sx: number; sy: number } {
  const p = toScreen(x, y, originX, originY);
  return { sx: p.sx, sy: p.sy + WORLD.TILE_H / 2 };
}

/** Inverse projection: screen point to fractional tile coordinates. */
export function toTile(sx: number, sy: number, originX: number, originY: number): { fx: number; fy: number } {
  const dx = (sx - originX) / (WORLD.TILE_W / 2);
  const dy = (sy - originY) / (WORLD.TILE_H / 2);
  return { fx: (dx + dy) / 2, fy: (dy - dx) / 2 };
}

/**
 * Eight facings in screen terms. On an isometric grid the four grid steps render as
 * the screen diagonals and the four grid diagonals render as screen N, E, S, W.
 * Rendering mirrors w, sw, nw from e, se, ne, so five sides are drawn.
 */
export type Facing = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

export interface Dir {
  dx: number;
  dy: number;
  facing: Facing;
  diagonal: boolean;
}

export const DIRS: Dir[] = [
  { dx: 1, dy: 0, facing: 'se', diagonal: false },
  { dx: -1, dy: 0, facing: 'nw', diagonal: false },
  { dx: 0, dy: 1, facing: 'sw', diagonal: false },
  { dx: 0, dy: -1, facing: 'ne', diagonal: false },
  { dx: 1, dy: 1, facing: 's', diagonal: true },
  { dx: -1, dy: -1, facing: 'n', diagonal: true },
  { dx: 1, dy: -1, facing: 'e', diagonal: true },
  { dx: -1, dy: 1, facing: 'w', diagonal: true },
];

export function facingFor(from: Tile, to: Tile): Facing {
  const dx = Math.sign(to.x - from.x);
  const dy = Math.sign(to.y - from.y);
  return facingForStep(dx, dy);
}

export function facingForStep(dx: number, dy: number): Facing {
  if (dx > 0 && dy > 0) return 's';
  if (dx < 0 && dy < 0) return 'n';
  if (dx > 0 && dy < 0) return 'e';
  if (dx < 0 && dy > 0) return 'w';
  if (dx > 0) return 'se';
  if (dx < 0) return 'nw';
  if (dy > 0) return 'sw';
  return 'ne';
}

/**
 * Convert a screen-space input vector (right = +x, down = +y, as from WASD) into a
 * grid step. Screen north is grid (−1, −1), screen east is grid (+1, −1), and so on.
 */
export function screenVectorToStep(sx: number, sy: number): { dx: number; dy: number } {
  return { dx: Math.sign(sx + sy), dy: Math.sign(sy - sx) };
}

export const DIAGONAL_COST = Math.SQRT2;

export type Walkable = (x: number, y: number) => boolean;

/** A diagonal step may not cut the corner of a blocked tile. */
export function canStep(walkable: Walkable, from: Tile, dx: number, dy: number): boolean {
  const nx = from.x + dx;
  const ny = from.y + dy;
  if (!walkable(nx, ny)) return false;
  if (dx !== 0 && dy !== 0) return walkable(from.x + dx, from.y) && walkable(from.x, from.y + dy);
  return true;
}

/**
 * Shortest path over walkable tiles with octile costs (1 for a grid step, √2 for a
 * diagonal). Returns the path excluding `start`, or null when no goal is reachable.
 */
export function findPath(walkable: Walkable, start: Tile, goals: Set<string>, maxNodes = 4096): Tile[] | null {
  if (goals.has(tileKey(start))) return [];
  const dist = new Map<string, number>();
  const prev = new Map<string, string | null>();
  const open: { key: string; tile: Tile; cost: number }[] = [{ key: tileKey(start), tile: start, cost: 0 }];
  dist.set(tileKey(start), 0);
  prev.set(tileKey(start), null);
  let visited = 0;
  while (open.length) {
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (open[i].cost < open[bi].cost) bi = i;
    const cur = open.splice(bi, 1)[0];
    if (cur.cost > (dist.get(cur.key) ?? Infinity)) continue;
    if (goals.has(cur.key)) {
      const path: Tile[] = [];
      let k: string | null = cur.key;
      while (k && k !== tileKey(start)) {
        const [px, py] = k.split(',').map(Number);
        path.push({ x: px, y: py });
        k = prev.get(k) ?? null;
      }
      return path.reverse();
    }
    if (++visited > maxNodes) break;
    for (const d of DIRS) {
      if (!canStep(walkable, cur.tile, d.dx, d.dy)) continue;
      const nx = cur.tile.x + d.dx;
      const ny = cur.tile.y + d.dy;
      const key = `${nx},${ny}`;
      const cost = cur.cost + (d.diagonal ? DIAGONAL_COST : 1);
      if (cost < (dist.get(key) ?? Infinity)) {
        dist.set(key, cost);
        prev.set(key, cur.key);
        open.push({ key, tile: { x: nx, y: ny }, cost });
      }
    }
  }
  return null;
}
